import {
    Box,
    Button,
    FormHelperText,
    Typography,
    useMediaQuery,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Config from "Config";
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { isNotEmpty } from "../../Utils";
import CustomTextField from "../../inputComponent/CustomTextField/CustomTextField";
import {
    getOTP,
    publicUserLogin,
    setPublicResetPasswordTime,
} from "../../redux/action";
import { getToken } from "../../redux/selector";
import ErrorMsg from "../ErrorMsg/ErrorMsg";
import "../LoginPage/LoginPage.scss";

const useStyles = makeStyles({
    eye: {
        transform: "translate(10px)",
    },
});

export default function LoginSSLODialog({ onLogin, type = "login" }) {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const matches = useMediaQuery("(min-width:600px)");
    const dispatch = useDispatch();

    const { register, handleSubmit, watch, formState, setError } = useForm({
        defaultValues: {
            email: "",
            password: "",
        },
        mode: "onBlur",
    });

    const token = useSelector(getToken());
    const [cookies, setCookie] = useCookies(["token"]);
    useEffect(() => {
        // when login success
        if (isNotEmpty(token)) {
            onLogin();
            setCookie("token", token, {
                path: `${Config.CONTENT_PATH}`,
                maxAge: 3600,
                secure: true,
            });
        }
    }, [token]);

    const onFetchError = () => {
        setError("password", {
            type: "invalid",
            message: t(`errorMsg.login.invalid`),
        });
    };

    const [isGenOTP, setIsGenOTP] = useState(false);
    const [otpMsg, setOtpMsg] = useState("");
    const [loginMsg, setLoginMsg] = useState("");
    const [passwordVisible, setPasswordVisible] = useState(false);

    const publicResetPassWordTime = useSelector(
        (state) => state.publicResetPassWordTime
    );
    const [otpButtonDisabled, setOtpButtonDisabled] = useState(false);
    const [countdown, setCountdown] = useState(0);

    const calculateWithin60Seconds = () => {
        if (!publicResetPassWordTime) return false;
        const currentTime = new Date().getTime();
        const timeDiff = Math.floor(
            (currentTime - publicResetPassWordTime) / 1000
        );
        return timeDiff <= 60;
    };

    useEffect(() => {
        if (publicResetPassWordTime && calculateWithin60Seconds()) {
            setOtpButtonDisabled(true);
            const timePassed = Math.floor(
                (new Date().getTime() - publicResetPassWordTime) / 1000
            );
            setCountdown(60 - timePassed);

            const interval = setInterval(() => {
                const timeDiff = Math.floor(
                    (new Date().getTime() - publicResetPassWordTime) / 1000
                );
                const remainingTime = 60 - timeDiff;

                if (remainingTime > 0) {
                    setCountdown(remainingTime);
                } else {
                    setCountdown(0);
                    setOtpButtonDisabled(false);
                    clearInterval(interval);
                }
            }, 1000);

            return () => {
                clearInterval(interval);
            };
        }
    }, [publicResetPassWordTime]);

    const onSubmit = (data, actionType) => {
        console.log("onsubmit", data);
        window.grecaptcha.ready(function () {
            window.grecaptcha
                .execute("6LeWvVwdAAAAAI9JwB5OcndiYHiCA_LCISNCmTiM", {
                    action: "submit",
                })
                .then(function (token) {
                    let formData = new FormData();
                    formData.append("email", data?.email);
                    formData.append("password", data?.password);
                    formData.append("response", token);
                    // Send form value as well as token to the server
                    if (actionType === "Login") {
                        if (data?.password === "") {
                            setLoginMsg("MISSING_PASSWORD");
                        } else {
                            dispatch(
                                publicUserLogin(formData, onFetchError)
                            ).then((result) => {
                                let msg = result?.msg;
                                setLoginMsg(msg);
                            });
                        }
                    } else {
                        dispatch(getOTP(formData, onFetchError)).then(
                            (result) => {
                                let msg = result?.msg;
                                if (msg == "SUCCESS") {
                                    const currentTime = new Date().getTime();
                                    dispatch(
                                        setPublicResetPasswordTime(currentTime)
                                    );
                                    setOtpButtonDisabled(true);
                                    setOtpMsg(msg);
                                    setIsGenOTP(true);
                                } else {
                                    setOtpMsg(msg);
                                }
                            }
                        );
                    }
                });
        });
    };

    const onClickVisible = () => {
        if (passwordVisible) {
            setPasswordVisible(false);
        } else {
            setPasswordVisible(true);
        }
    };

    const errors = formState.errors;

    return (
        <Box width={400}>
            <Box
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                <Typography variant="h5">
                    {t("common.portal.publicLogin")}
                    <br />
                    <br />
                </Typography>
                <CustomTextField
                    label={t("common.email")}
                    required={false}
                    inputProps={register("email", {
                        validate: {
                            valid: (value) =>
                                value.includes("@") || t("errorMsg.email.rule"),
                        },
                        required: t("errorMsg.email.rule"),
                    })}
                    // type="email"
                />
                <FormHelperText>
                    <ErrorMsg message={errors?.email?.message} />
                </FormHelperText>
                {otpMsg != "SUCCESS" && otpMsg != "" && (
                    <FormHelperText>
                        <ErrorMsg
                            message={
                                otpMsg === "NOT_PUBLIC_USER"
                                    ? t("common.portal.notPublicUser")
                                    : otpMsg === "INCORRECT_EMAIL"
                                    ? t("errorMsg.email.rule")
                                    : null
                            }
                        />
                    </FormHelperText>
                )}
                {isGenOTP && (
                    <CustomTextField
                        label={t("common.portal.password")}
                        required={false}
                        type={passwordVisible ? null : "password"}
                        inputProps={register("password")}
                        unit={
                            passwordVisible === true ? (
                                <Button
                                    onClick={onClickVisible}
                                    disableRipple={true}
                                    className={classes.eye}
                                >
                                    <VisibilityOffIcon
                                        style={{ color: "gray" }}
                                    />
                                </Button>
                            ) : (
                                <Button
                                    onClick={onClickVisible}
                                    disableRipple={true}
                                    className={classes.eye}
                                >
                                    <VisibilityIcon style={{ color: "gray" }} />
                                </Button>
                            )
                        }
                    />
                )}
                {loginMsg != "SUCCESS" && (
                    <FormHelperText>
                        <ErrorMsg
                            message={
                                loginMsg === "INVALID_USER_OR_PW"
                                    ? t("common.portal.invalidLogin")
                                    : loginMsg === "EMAIL_MISSING"
                                    ? t("errorMsg.email.rule")
                                    : loginMsg === "PW_MISSING"
                                    ? t("common.portal.missingPw")
                                    : loginMsg === "NOT_PUBLIC_USER"
                                    ? t("common.portal.notPublicUser")
                                    : loginMsg === "ACCOUNT_LOCKED"
                                    ? t("common.login.error.accountLocked")
                                    : otpMsg === "COOLDOWN"
                                    ? t("common.portal.cooldown")
                                    : null
                            }
                        />
                    </FormHelperText>
                )}
                <br />
                <br />
                <Box>
                    {isGenOTP && (
                        <Button
                            className="buttons"
                            // component={Link}
                            variant="contained"
                            color="primary"
                            //   type="submit"
                            style={{ marginRight: "20px" }}
                            onClick={handleSubmit((data) =>
                                onSubmit(data, "Login")
                            )}
                            data-action="submit"
                            data-sitekey="6LeWvVwdAAAAAI9JwB5OcndiYHiCA_LCISNCmTiM"
                            data-callback="onSubmit"
                        >
                            {t("common.sideNavList.login")}
                        </Button>
                    )}
                    <Button
                        className="buttons"
                        variant="contained"
                        color="primary"
                        //  type="submit"
                        onClick={handleSubmit((data) => onSubmit(data, "OTP"))}
                        disabled={otpButtonDisabled}
                    >
                        {isGenOTP
                            ? `${t("common.portal.resendOTP")}${
                                  otpButtonDisabled && countdown > 0
                                      ? ` ${countdown}${
                                            i18n.language.includes("tc") ||
                                            i18n.language.includes("sc")
                                                ? "秒"
                                                : "s"
                                        }`
                                      : ""
                              }`
                            : `${t("common.portal.getOTP")}${
                                  otpButtonDisabled && countdown > 0
                                      ? ` ${countdown}${
                                            i18n.language.includes("tc") ||
                                            i18n.language.includes("sc")
                                                ? "秒"
                                                : "s"
                                        }`
                                      : ""
                              }`}
                    </Button>
                </Box>
            </Box>
            <div className={"dialog-page"}>
                {t("reCaptcha.lng")}
                <a
                    href={t("reCaptcha.policyLink")}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t("reCaptcha.priPolicy")}
                </a>{" "}
                {t("reCaptcha.and")}
                <a
                    href={t("reCaptcha.termsLink")}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t("reCaptcha.terService")}
                </a>{" "}
                {t("reCaptcha.apply")}
                {/*</p>*/}
            </div>
        </Box>
    );
}
